/**
 * Self Assessment Reviews Component
 * Provides access to ARCaml self-assessment documents behind the paywall.
 * These contain ARCaml IP and are only available to authenticated/subscribed users.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useMyAccountData } from '../../hooks/useMyAccountData';
import { getAustracDocuments, S3Document } from '../../services/agreementService';
import { logger } from '../../utils/logger';
import { formatSectorLabel } from '../../utils/sectorLabels';
import PortalHeader from './PortalHeader';
import './SelfAssessment.css';

const isDocx = (fileName: string) => fileName.toLowerCase().endsWith('.docx');

const displayName = (fileName: string) =>
  fileName
    .replace(/\.(pdf|docx)$/i, '')
    .replace(/[_-]/g, ' ');

/**
 * Label forms that identify an organisation whose sector is "Other".
 *
 * We have to match on labels because the machine value never reaches us: the
 * signup app's resolveEffectiveSector() (arcaml-c1-signup/src/hooks/useGoLive.ts:116-131)
 * converts partySector into a human display label before sending it — for
 * "Other" that is the chosen sub-option's label, or the bare string "Other" —
 * and discards the machine value `other`. The label is persisted to
 * organizations.industry_description and arrives here as accountData.sector /
 * services / reportingEntityType, so the label is all we have to go on.
 *
 * Entries are verbatim (lowercased) from
 * arcaml-c1-signup/src/hooks/usePartyDetailsForm.ts — the 'other' entry of
 * SECTOR_OPTIONS plus all 11 OTHER_SECTOR_SUB_OPTIONS labels — with the ABN
 * 'Other Entity' literal added. A new sub-option upstream is one new line here;
 * __tests__/selfAssessmentSectorFolder.test.tsx pins the expected contents so a
 * divergence fails in CI rather than silently emptying a customer's page.
 *
 * Free-text entries (the "other_specify:" path) cannot be enumerated; those
 * sectors do not resolve and the page fails closed — see the folder check in
 * the fetch effect below.
 */
export const OTHER_SECTOR_LABELS = new Set<string>([
  // Sector itself
  'other',
  'other (afsl holders, adis, and other austrac-enrolled entities)',
  // ABN-derived legal entity type literal (useAbnLookup.ts), also means "Other".
  // Correct only because the candidate order consults the sector fields ahead of
  // reportingEntityType (see the folder memo below); read first, this literal
  // would pull a mapped-sector org into the Other pack.
  'other entity',
  // The 11 "Other" sub-options
  'banking and deposit-taking institutions — banks, building societies, credit unions',
  'non-bank lenders and finance companies',
  'financial planning',
  'foreign exchange and remittance services — currency exchange, money transfer businesses',
  'securities and derivatives dealers — stockbrokers, futures dealers, margin lenders',
  'managed investment scheme operators / responsible entities',
  'superannuation trustees',
  'life insurance companies and intermediaries',
  'digital currency exchange providers',
  'bullion dealers',
  'casinos and wagering/betting service providers',
]);

/** Map any org sector / industry strings → austrac S3 folder name */
export function resolveFolder(...inputs: (string | undefined)[]): string {
  const candidates = inputs.map((v) => (v || '').trim().toLowerCase());
  for (const v of candidates) {
    if (!v) continue;
    // "Other" is tested before the keyword branches, and on whole labels, by
    // design: two sub-option labels collide with keywords below — "Bullion
    // dealers" with `bullion` (→ precious-metals) and "Superannuation
    // trustees" with `trust` (→ tsu) — so an Other org would be served another
    // sector's pack if this ran second.
    if (OTHER_SECTOR_LABELS.has(v)) return 'other';
    if (v === 'accounting' || v.includes('accounting') || v.includes('accountant') || v.includes('tax')) return 'accounting';
    if (v === 'legal' || v.includes('legal') || v.includes('law') || v.includes('solicitor')) return 'legal';
    if (v === 'conveyancing' || v.includes('conveyancing') || v.includes('conveyancer')) return 'conveyancing';
    if (v === 'real-estate' || v.includes('real estate') || v.includes('real-estate') || v.includes('property')) return 'real-estate';
    if (v === 'bullion_dealer' || v.includes('precious') || v.includes('bullion') || v.includes('metal') || v.includes('gold')) return 'precious-metals';
    if (v === 'tsu' || v.includes('trust') || v.includes('company service')) return 'tsu';
  }
  return '';
}

const DocxPreviewPane: React.FC<{ downloadUrl: string; fileName: string }> = ({ downloadUrl, fileName }) => {
  const [loaded, setLoaded] = useState(false);
  // Google's servers fetch from S3 — bypasses browser CORS restriction entirely
  const src = `https://docs.google.com/viewer?url=${encodeURIComponent(downloadUrl)}&embedded=true`;
  return (
    <>
      {!loaded && (
        <div className="sa-pdf-loading">
          <div className="sa-spinner" />
          <p>Loading document…</p>
        </div>
      )}
      <iframe
        key={downloadUrl}
        src={src}
        title={fileName}
        style={{ width: '100%', height: 700, border: 'none', display: loaded ? 'block' : 'none' }}
        onLoad={() => setLoaded(true)}
      />
    </>
  );
};

const SelfAssessment: React.FC = () => {
  const { accountData } = useMyAccountData();
  const [documents, setDocuments] = useState<S3Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocKey, setSelectedDocKey] = useState<string | null>(null);
  const [selectedDocUrl, setSelectedDocUrl] = useState<string>('');
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Sector fields first, legal structure last. reportingEntityType holds the
  // ABN-derived legal STRUCTURE (Trust, Discretionary Trust, Unit Trust, …,
  // Other Entity — useAbnLookup.ts:39-50), not a sector, so while it was read
  // first every trust-structured org matched the `trust` keyword and was served
  // the TSU pack before its real sector was consulted. It is kept as a last
  // resort rather than dropped because it is the only field that can carry the
  // 'Other Entity' signal, and because accountData.sector falls back to it
  // anyway (useMyAccountData.helpers.ts:30) — services (= industryDescription)
  // is the only candidate that reliably holds the sector.
  const folder = useMemo(
    () => resolveFolder(accountData.services, accountData.sector, accountData.reportingEntityType),
    [accountData.services, accountData.sector, accountData.reportingEntityType],
  );

  const sectorLabel = folder ? formatSectorLabel(folder) : null;

  useEffect(() => {
    if (!accountData.accountId) return;
    // Fail closed on an unresolved sector. getAustracDocuments('') widens the
    // prefix to the austrac/ root (agreementService.ts:139) and returns every
    // sector's pack — deliberate for the signup app's "fetch everything" call,
    // wrong here, where showing another sector's compliance documents is worse
    // than showing none.
    if (!folder) {
      // logger.error, not warn: CURRENT_LOG_LEVEL is LOG_LEVEL.ERROR
      // (config/constants.ts), so anything below error is dropped in every
      // environment and this path would be silent everywhere.
      logger.error('[SelfAssessment] Sector did not resolve to a document folder; showing none', {
        reportingEntityType: accountData.reportingEntityType,
        sector: accountData.sector,
        services: accountData.services,
      });
      setDocuments([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAustracDocuments(folder)
      .then((docs) => {
        if (cancelled) return;
        if (docs.length === 0) {
          // Resolved to a folder, but the folder holds nothing: a catalogue gap,
          // not a customer condition. austrac/other/ exists in dev1, dev2, test
          // and demo with its 3 documents but is absent from the live bucket, so
          // until it is uploaded this is what live Other orgs hit. Logged at
          // error level for the same reason as above, and kept out of the UI —
          // the customer sees the same calm empty state either way.
          logger.error('[SelfAssessment] Sector resolved to a folder with no documents', {
            folder,
            reportingEntityType: accountData.reportingEntityType,
            sector: accountData.sector,
            services: accountData.services,
          });
        }
        setDocuments(docs);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [folder, accountData.accountId]);

  const selectedDoc = documents.find(doc => doc.key === selectedDocKey);

  const handleDocumentClick = (docKey: string) => {
    const doc = documents.find(d => d.key === docKey);
    if (!doc) return;
    setSelectedDocKey(docKey);
    setSelectedDocUrl(doc.downloadUrl);
    setPdfError(null);
  };

  const handleClosePdf = () => {
    setSelectedDocKey(null);
    setSelectedDocUrl('');
    setPdfError(null);
  };

  return (
    <div className="self-assessment-container">
      <PortalHeader />
      <div className="self-assessment-wrapper">
        <div className="self-assessment-header">
          <div className="relative pl-5 mb-8">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-cyan-500 to-teal-600 rounded-full" />
            <h1>Self Assessment Reviews</h1>
            <p className="header-subtitle">
              Review and assess your organization's compliance posture using ARCaml's proprietary assessment frameworks.
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="sa-info-banner">
          <div className="sa-info-icon">ℹ️</div>
          <div className="sa-info-text">
            <strong>About Self Assessments</strong>
            <p>
              These documents contain ARCaml's proprietary compliance assessment frameworks.
              Use them to evaluate your organization's AML/CTF program effectiveness and identify areas for improvement.
            </p>
            {sectorLabel && (
              <p style={{ marginTop: 6, marginBottom: 0 }}>
                Showing documents for: <strong>{sectorLabel}</strong>
              </p>
            )}
          </div>
        </div>

        {/* Document Cards */}
        {!selectedDoc && (
          <div className="sa-documents-grid">
            {loading ? (
              <div className="sa-loading">
                <div className="sa-spinner" />
                <p>Loading documents...</p>
              </div>
            ) : documents.length === 0 ? (
              <div className="sa-empty">
                <p>No self-assessment documents available at this time.</p>
              </div>
            ) : (
              documents.map((doc, idx) => (
                <div
                  key={doc.key || `doc-${idx}`}
                  className="sa-document-card"
                  onClick={() => handleDocumentClick(doc.key)}
                >
                  <div className="sa-card-icon">📋</div>
                  <div className="sa-card-content">
                    <h3>{displayName(doc.fileName)}</h3>
                    {doc.lastModified && <p>Last updated {new Date(doc.lastModified).toLocaleDateString('en-AU')}</p>}
                  </div>
                  <div className="sa-card-action">
                    <span className="sa-view-btn">View Document →</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Document Viewer */}
        {selectedDoc && (
          <div className="sa-pdf-viewer">
            <div className="sa-pdf-toolbar">
              <button className="sa-back-btn" onClick={handleClosePdf}>
                ← Back to Documents
              </button>
              <h3 className="sa-pdf-title">{displayName(selectedDoc.fileName)}</h3>
              <div className="sa-pdf-controls">
                <button
                  className="sa-download-btn"
                  title="Download document"
                  disabled={!selectedDocUrl}
                  onClick={() => {
                    if (selectedDocUrl) window.open(selectedDocUrl, '_blank', 'noopener,noreferrer');
                  }}
                >
                  ⬇ Download
                </button>
              </div>
            </div>

            <div className="sa-pdf-content">
              {pdfError ? (
                <div className="sa-pdf-error">
                  <span>⚠️</span>
                  <p>{pdfError}</p>
                  <button onClick={() => setPdfError(null)}>Try Again</button>
                </div>
              ) : selectedDocUrl ? (
                isDocx(selectedDoc.fileName) ? (
                  <DocxPreviewPane downloadUrl={selectedDocUrl} fileName={selectedDoc.fileName} />
                ) : (
                  <iframe
                    src={`${selectedDocUrl}#toolbar=1&navpanes=0`}
                    title={selectedDoc.fileName}
                    style={{ width: '100%', height: 700, border: 'none', display: 'block' }}
                    onError={() => setPdfError('Failed to load the document. Please try again.')}
                  />
                )
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelfAssessment;
